import { SectionHeader } from '../common/section-header'
import { useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom'

import { AdminApiError } from '../api/client.js'
import { queryKeys } from '../api/query-keys.js'
import { Badge, Icon, Notice, NoticeAction } from '@speaking-coach/ui'
import { AvailabilityBadge, ContentStatusBadge } from '../activity/content-history-badges.js'
import { formatActivityTimestamp } from '../activity/activity-presentation.js'
import { adminContentClient } from './api.js'
import {
  CONTENT_HELP,
  ContentMetricLabel,
  expectedComplicationsHelp,
  sentencePercentileHelp,
} from './content-help.js'
import {
  allTopics,
  badgeVariant,
  contentTopicParentDestination,
  curriculumReturnContext,
  labelForStatus,
  topicHref,
  topicReviewSummary,
  vocabularyReturnContext,
} from './content-model.js'
import { ContentHistoryLink, ContentHistoryPreview } from './content-history-preview'
import { ContentDetailShell, ContentKeyBadge } from './content-shell.js'
import { RevisionSummaryFields, RevisionsSection } from './revision-pages.js'
import type {
  ContentLevel,
  ContentTheme,
  ContentTopic,
  ContentTopicSource,
  SentenceSignal,
  TargetVocabulary,
} from './types.js'

export function ContentTopicPage(): JSX.Element {
  const { topicId } = useParams<{ topicId: string }>()
  if (!topicId) return <ContentDetailShell title="Lesson not found"><Notice variant="neutral">This lesson could not be found.</Notice></ContentDetailShell>
  return <TopicPage topicId={topicId} />
}

function TopicPage({ topicId }: { topicId: string }): JSX.Element {
  const [searchParams] = useSearchParams()
  const query = useQuery({
    queryKey: queryKeys.contentTopicSource(topicId),
    queryFn: () => adminContentClient.getTopicSource(topicId),
    retry: false,
  })
  const level = query.data?.topicId === topicId ? query.data.level : null
  const rows = level ? allTopics({ levels: [level] }) : []
  const topicIndex = rows.findIndex((row) => row.topic.id === topicId)
  const location = rows[topicIndex]
  const parentDestination = contentTopicParentDestination(
    searchParams,
    location
      ? { level: location.level.programLevelId, theme: location.theme.id }
      : undefined,
  )
  const origin =
    parentDestination.kind === 'coverage' ||
    parentDestination.kind === 'vocabulary'
      ? parentDestination.kind
      : undefined
  const returnContext = searchParams.has('returnTo')
    ? (origin === 'vocabulary'
        ? vocabularyReturnContext
        : curriculumReturnContext)(
        new URLSearchParams(searchParams.get('returnTo') ?? ''),
      )
    : undefined
  const requestedFromLevel = searchParams.get('fromLevel') ?? ''
  const originLevel = requestedFromLevel || location?.level.programLevelId
  const blockerLabel = query.data?.blockingParent === 'theme'
    ? location?.theme.title.en
    : query.data?.blockingParent === 'program'
      ? location?.level.programName.en
      : null

  if (query.isPending) {
    return <ContentDetailShell title="Lesson"><p data-testid="content-loading" className="text-body text-text-secondary">Loading lesson…</p></ContentDetailShell>
  }

  if (query.isError) {
    const notFound =
      query.error instanceof AdminApiError && query.error.status === 404
    return (
      <ContentDetailShell title={notFound ? 'Lesson not found' : 'Lesson'}>
        <Notice variant={notFound ? 'neutral' : 'danger'} action={notFound ? undefined : <NoticeAction onClick={() => void query.refetch()}>Try again</NoticeAction>}>
          {notFound ? 'This lesson could not be found.' : 'Could not load this lesson.'}
        </Notice>
      </ContentDetailShell>
    )
  }

  if (!query.data || !location) {
    return <ContentDetailShell title="Lesson not found"><Notice variant="neutral">This lesson could not be found.</Notice></ContentDetailShell>
  }

  return (
    <ContentDetailShell title={location.topic.title.en} action={<ContentHistoryLink targetType="scenario" targetId={location.topic.id} />}>
      <div className="space-y-4">
        <TopicDetail
          level={location.level}
          theme={location.theme}
          topic={location.topic}
          source={query.data}
          blockerLabel={blockerLabel}
        />
        <RevisionsSection kind="lesson" identityId={location.topic.id} />
        <ContentHistoryPreview targetType="scenario" targetId={location.topic.id} />
        <nav aria-label="Adjacent lessons" className="grid gap-3 sm:grid-cols-2">
          {rows[topicIndex - 1] ? (
            <Link
              to={topicHref(rows[topicIndex - 1]!, returnContext, origin, origin ? originLevel : undefined)}
              aria-label={`Previous lesson: ${rows[topicIndex - 1]!.topic.title.en}`}
              className="flex items-center gap-3 rounded-token-card border border-border-default bg-surface-elev p-4 hover:border-accent"
            >
              <Icon name="previous" size={20} className="shrink-0" />
              <span><span className="block text-caption text-text-secondary">Previous lesson</span><span className="font-semibold">{rows[topicIndex - 1]!.topic.title.en}</span></span>
            </Link>
          ) : <span />}
          {rows[topicIndex + 1] ? (
            <Link
              to={topicHref(rows[topicIndex + 1]!, returnContext, origin, origin ? originLevel : undefined)}
              aria-label={`Next lesson: ${rows[topicIndex + 1]!.topic.title.en}`}
              className="flex items-center justify-end gap-3 rounded-token-card border border-border-default bg-surface-elev p-4 text-right hover:border-accent"
            >
              <span><span className="block text-caption text-text-secondary">Next lesson</span><span className="font-semibold">{rows[topicIndex + 1]!.topic.title.en}</span></span>
              <Icon name="next" size={20} className="shrink-0" />
            </Link>
          ) : null}
        </nav>
      </div>
    </ContentDetailShell>
  )
}

function TopicDetail({
  level,
  theme,
  topic,
  source,
  blockerLabel,
}: {
  level: ContentLevel
  theme: ContentTheme
  topic: ContentTopic
  source: ContentTopicSource
  blockerLabel: string | null | undefined
}): JSX.Element {
  const review = topicReviewSummary(level, topic, source.priorTargetLemmas)
  const { hash, key: locationKey } = useLocation()

  useEffect(() => {
    if (!hash.startsWith('#lesson-source-line-')) return

    document.getElementById(hash.slice(1))?.scrollIntoView({ block: 'center' })
  }, [hash, locationKey, source.sourceMarkdown])

  return (
    <article
      data-testid="content-topic-detail"
      className="min-w-0 space-y-6 rounded-token-card border border-border-default bg-surface-elev p-5"
    >
      <header className="space-y-2">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <ContentKeyBadge value={source.contentKey} />
            <Badge variant={badgeVariant(topic.contentStatus)}>
              Authoring source: {labelForStatus(topic.contentStatus)}
            </Badge>
            <span className="text-caption text-text-secondary">
              {level.level} · T{theme.position} · #{topic.position} ·{' '}
              {theme.title.en}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {source.status ? <ContentStatusBadge status={source.status} /> : null}
            <AvailabilityBadge available={source.available ?? null} />
          </div>
        </div>
        {blockerLabel ? <p className="text-body text-text-secondary">Availability is blocked by {blockerLabel}.</p> : null}
        {source.revisionIssue ? <Notice variant="danger">The active revision cannot be read by this application version. This lesson is blocked from new calls until the revision is corrected or the application is updated.</Notice> : null}
        <div className="grid gap-4 text-body sm:grid-cols-2">
          <dl data-slot="bilingual-title-row" className="grid gap-4 sm:col-span-2 sm:grid-cols-2"><div><dt className="text-text-secondary">English title</dt><dd className="mt-1 text-text-primary">{topic.title.en}</dd></div><div><dt className="text-text-secondary">Japanese title</dt><dd className="mt-1 text-text-primary">{topic.title.ja || 'Not recorded'}</dd></div></dl>
          <RevisionSummaryFields kind="lesson" identityId={topic.id} repositorySourcePath={source.sourceFile} />
          <dl data-slot="content-timestamps" className="grid gap-4 sm:col-span-2 sm:grid-cols-2"><div><dt className="text-text-secondary">Created</dt><dd className="mt-1 text-text-primary">{formatActivityTimestamp(source.createdAt)}</dd></div><div><dt className="text-text-secondary">Last updated</dt><dd className="mt-1 text-text-primary">{formatActivityTimestamp(source.updatedAt)}</dd></div></dl>
        </div>
      </header>

      <section
        aria-labelledby="can-do-heading"
        className="rounded-token-card border border-accent bg-surface-app p-5"
      >
        <div className="flex items-center gap-2 text-label font-medium uppercase tracking-wide text-text-secondary">
          <Icon name="language" />
          <span id="can-do-heading">CAN DO</span>
        </div>
        <p className="mt-3 font-display text-section-title font-semibold md:text-product-title">
          {topic.canDo.en}
        </p>
        <p className="mt-2 text-body text-text-primary">
          <span className="mr-2 text-label font-medium uppercase tracking-wide text-text-secondary">Japanese</span>
          {topic.canDo.ja}
        </p>
      </section>

      <div className="grid gap-3 md:grid-cols-2">
        <div className="rounded-token-card border border-border-default bg-surface-app p-4">
          <div className="flex items-center gap-2 text-label font-medium uppercase tracking-wide text-text-secondary">
            <Icon name="teachingFrame" /> Teaching frame
          </div>
          <p className="mt-2 font-medium">{topic.grammarFocus ?? 'Not recorded'}</p>
          <p className="mt-1 text-body text-text-secondary">
            {level.band.grammarInventory}
          </p>
        </div>
        <div className="rounded-token-card border border-border-default bg-surface-app p-4">
          <p className="text-label font-medium uppercase tracking-wide text-text-secondary">
            Learner-level fit · {level.level}
          </p>
          <p className={`mt-2 font-semibold ${review.needsReview ? 'text-danger-text' : ''}`}>
            {review.needsReview
              ? 'Needs content-team review'
              : 'Within measured level limits'}
          </p>
          <p className="mt-1 text-body text-text-secondary">
            {review.needsReview
              ? 'Resolve the measured signals below, then confirm that the CAN DO is realistic and functional for the target learner.'
              : 'Automated checks are clear. Human review must still confirm that the CAN DO is realistic and functional for the target learner.'}
          </p>
          {review.needsReview ? (
            <ul
              aria-label="Review findings"
              className="mt-3 list-disc space-y-1 pl-5 text-body text-danger-text"
            >
              {review.findings.map((finding) => (
                <li key={finding}>{finding}</li>
              ))}
            </ul>
          ) : null}
          <p className="mt-2 text-caption text-text-secondary">
            {review.quotaCount} / {level.band.inWindowQuota} unique quota-counting
            targets · {review.duplicateSummary} · duration{' '}
            {review.durationMatches ? 'matches' : 'differs from'} the {level.level}{' '}
            contract
          </p>
        </div>
      </div>

      <section aria-labelledby="task-shape-heading">
        <SectionHeader
          level={3}
          id="task-shape-heading"
          title="Mission and task shape"
        />
        <div className="mt-3 grid gap-3 text-body sm:grid-cols-2 lg:grid-cols-4">
          <Evidence
            label="Missions"
            value={`${topic.taskShape.missionCount} / ${topic.taskShape.expectedMissionCount}`}
            attention={!review.missionMatches}
            help={CONTENT_HELP.missions}
          />
          <Evidence
            label="Expected complications"
            value={topic.taskShape.expectedComplications}
            help={expectedComplicationsHelp(
              level.level,
              topic.taskShape.expectedComplications,
            )}
          />
          <Evidence
            label="Expected capstone"
            value={topic.taskShape.expectedCapstoneIntegration}
            help={CONTENT_HELP.expectedCapstone}
          />
          <Evidence
            label="Target duration"
            value={`${Math.round(topic.taskShape.targetDurationSec / 60)} min · expected ${Math.round(level.band.targetDurationSec / 60)} min`}
            attention={!review.durationMatches}
          />
        </div>
      </section>

      <section aria-labelledby="difficulty-heading">
        <SectionHeader
          level={3}
          id="difficulty-heading"
          title="Difficulty evidence"
          meta={<Icon name="difficulty" className="text-text-secondary" />}
          className="mb-3"
        />
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <Evidence
            label="Sentence p50"
            value={`${topic.sentenceStats.p50Words} words`}
            help={sentencePercentileHelp(50)}
          />
          <Evidence
            label="Sentence p90"
            value={`${topic.sentenceStats.p90Words} words`}
            help={sentencePercentileHelp(90)}
          />
          <Evidence
            label="Sentence p95"
            value={`${topic.sentenceStats.p95Words} words`}
            help={sentencePercentileHelp(95)}
          />
          <Evidence
            label="Sentence max"
            value={`${topic.sentenceStats.maxWords} words`}
            help={CONTENT_HELP.sentenceMaximum}
          />
          <Evidence
            label="Above-band targets"
            value={
              review.aboveBandCap === null
                ? `${review.aboveBandCount} · C1+ needs approved exception`
                : `${review.aboveBandCount} / ${review.aboveBandCap} cap`
            }
            attention={review.aboveBandNeedsAttention}
            help={CONTENT_HELP.aboveBandTargets}
          />
          <SentenceLengthChecks
            level={level}
            topic={topic}
            signals={source.sentenceSignals}
          />
        </div>
      </section>

      <VocabularyTable vocabulary={topic.targetVocab} />
      <TopicSource level={level.level} sourceMarkdown={source.sourceMarkdown} />
    </article>
  )
}

function SentenceLengthChecks({
  level,
  topic,
  signals,
}: {
  level: ContentLevel
  topic: ContentTopic
  signals: SentenceSignal[]
}): JSX.Element {
  const hardCount = topic.sentenceStats.aboveHardCount
  const advisoryOnlyCount = Math.max(
    0,
    topic.sentenceStats.aboveAdvisoryCount - hardCount,
  )
  const affectedCount = advisoryOnlyCount + hardCount
  const resultTone =
    hardCount > 0
      ? 'text-danger-text'
      : advisoryOnlyCount > 0
        ? 'text-warning-text'
        : 'text-text-primary'

  return (
    <section
      aria-labelledby="sentence-length-checks-heading"
      className="rounded-token-card border border-border-default bg-surface-app p-4 sm:col-span-2 xl:col-span-4"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h4
            id="sentence-length-checks-heading"
            className="font-display font-semibold"
          >
            Sentence-length checks · {level.level}
          </h4>
          <p className={`mt-1 font-semibold ${resultTone}`}>
            {advisoryOnlyCount} above guidance · {hardCount} over maximum
          </p>
        </div>
        <p className="text-body text-text-secondary">
          {level.level} guidance recommends {level.band.advisorySentenceWords}{' '}
          words or fewer. The hard maximum is {level.band.hardSentenceWords}{' '}
          words.
        </p>
      </div>
      <p className="mt-2 text-caption text-text-secondary">
        English words and contractions are counted. A sentence is flagged only
        when it is longer than the stated threshold.
      </p>
      {affectedCount > 0 ? (
        <details className="mt-4 border-t border-border-default pt-3">
          <summary className="cursor-pointer text-body font-semibold text-text-primary hover:underline">
            Review {affectedCount} affected{' '}
            {affectedCount === 1 ? 'sentence' : 'sentences'}
          </summary>
          {signals.length === 0 ? (
            <Notice variant="warning" className="mt-3">
              The aggregate counts are available, but the affected sentence
              details could not be loaded.
            </Notice>
          ) : (
            <ol className="mt-3 space-y-3">
              {signals.map((signal, index) => (
                <SentenceSignalRow
                  key={`${signal.sourceLine}:${signal.text}:${index}`}
                  signal={signal}
                />
              ))}
            </ol>
          )}
        </details>
      ) : (
        <p className="mt-3 text-body text-text-secondary">
          No measured learner-facing sentence exceeds the {level.level}{' '}
          guidance.
        </p>
      )}
    </section>
  )
}

function SentenceSignalRow({ signal }: { signal: SentenceSignal }): JSX.Element {
  const isHard = signal.severity === 'hard'
  const { search } = useLocation()
  return (
    <li className="rounded-token-card border border-border-default bg-surface-elev p-3">
      <div className="flex flex-wrap items-center gap-2 text-caption">
        <span
          className={`rounded border px-2 py-0.5 font-semibold ${
            isHard
              ? 'border-danger/40 bg-danger/10 text-danger-text'
              : 'border-warning/50 bg-warning/10 text-warning-text'
          }`}
        >
          {isHard ? 'Hard limit' : 'Advisory'}
        </span>
        <span className="font-medium">{signal.wordCount} words</span>
        <span className="text-text-secondary">
          {signal.sourceContext} · line {signal.sourceLine}
        </span>
      </div>
      <blockquote className="mt-2 border-l-2 border-border-default pl-3 text-body">
        {signal.text}
      </blockquote>
      <Link
        to={{ search, hash: `lesson-source-line-${signal.sourceLine}` }}
        aria-label={`View sentence at lesson source line ${signal.sourceLine}`}
        className="mt-2 inline-flex text-caption font-medium text-text-primary hover:underline"
      >
        View in Lesson Markdown
      </Link>
    </li>
  )
}

function TopicSource({
  level,
  sourceMarkdown,
}: {
  level: string
  sourceMarkdown: string
}): JSX.Element {
  const guidelinesSearch = new URLSearchParams({ level })

  return (
    <section aria-labelledby="lesson-markdown-heading">
      <SectionHeader
        level={3}
        id="lesson-markdown-heading"
        title="Lesson Markdown"
        actions={<Link
          to={`/admin/content/authoring-guidelines?${guidelinesSearch.toString()}`}
          className="inline-flex items-center gap-2 text-body font-medium text-text-primary hover:underline"
        >
          <Icon name="notes" />
          Open {level} authoring guidelines
        </Link>}
      />
      {sourceMarkdown.length === 0 ? (
        <Notice variant="neutral" className="mt-3">
          No lesson Markdown is recorded for this lesson.
        </Notice>
      ) : (
        <pre className="mt-3 max-h-[40rem] overflow-auto rounded-token-card border border-border-default bg-surface-app p-4 font-mono text-caption text-text-primary">
          <code data-testid="content-topic-source">
            {sourceMarkdown.split('\n').map((line, index, lines) => (
              <span
                id={`lesson-source-line-${index + 1}`}
                key={`lesson-source-line-${index + 1}`}
                className="scroll-mt-24 target:bg-warning/10"
              >
                {line}
                {index < lines.length - 1 ? '\n' : ''}
              </span>
            ))}
          </code>
        </pre>
      )}
    </section>
  )
}

export function Evidence({
  label,
  value,
  attention = false,
  help,
}: {
  label: string
  value: string
  attention?: boolean
  help?: string
}): JSX.Element {
  return (
    <div className="rounded-token-card border border-border-default bg-surface-app p-3">
      <ContentMetricLabel label={label} help={help} />
      <p className={`mt-1 font-semibold ${attention ? 'text-danger-text' : ''}`}>
        {value}
      </p>
    </div>
  )
}

function VocabularyTable({
  vocabulary,
}: {
  vocabulary: TargetVocabulary[]
}): JSX.Element {
  return (
    <section aria-labelledby="target-vocabulary-heading">
      <SectionHeader
        level={3}
        id="target-vocabulary-heading"
        title="Target vocabulary"
      />
      <div className="mt-3 overflow-x-auto rounded-token-card border border-border-default">
        <table className="w-full min-w-[38rem] border-collapse text-left text-table">
          <thead className="bg-surface-app text-[10.5px] leading-4 uppercase tracking-wide text-text-secondary">
            <tr>
              <th className="px-3 py-2 font-medium">Lemma</th>
              <th className="px-3 py-2 font-medium">Japanese</th>
              <th className="px-3 py-2 font-medium">Grade</th>
              <th className="px-3 py-2 font-medium">Band decision</th>
            </tr>
          </thead>
          <tbody>
            {vocabulary.map((item, index) => (
              <tr
                key={`${item.canonicalLemma}:${item.en}:${index}`}
                className="border-t border-border-default align-top"
              >
                <td className="px-3 py-3 font-medium">{item.en}</td>
                <td className="px-3 py-3 text-text-secondary">{item.ja}</td>
                <td className="px-3 py-3">{item.bandStatus === 'invalid' ? 'Not analyzed' : item.cefrLevel ?? 'Ungraded'}</td>
                <td className="px-3 py-3">
                  <Badge
                    variant={
                      item.bandStatus !== 'in_window' && !item.waived
                        ? 'danger'
                        : 'muted'
                    }
                  >
                    {item.bandStatus.replace('_', ' ')}
                    {item.waived ? ' · approved exception' : ''}
                  </Badge>
                  {item.invalidReason ? (
                    <p className="mt-1 max-w-md text-table-secondary text-danger-text">{item.invalidReason}. Correct the source content; excluded from grading totals.</p>
                  ) : null}
                  {item.waiverJustification ? (
                    <p className="mt-1 max-w-md text-table-secondary text-text-secondary">
                      {item.waiverJustification}
                    </p>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}
